// Utilidades de validación para datos de entrada

/**
 * Valida una fecha de nacimiento
 * @param {string} birthDate - Fecha en formato YYYY-MM-DD
 * @returns {boolean} - true si es válida
 */
export function validateBirthDate(birthDate) {
  if (!birthDate || typeof birthDate !== 'string') {
    return false;
  }

  // Verificar formato YYYY-MM-DD
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(birthDate)) {
    return false;
  }

  // Verificar que sea una fecha válida
  const date = new Date(birthDate);
  if (isNaN(date.getTime())) {
    return false;
  }

  // Verificar que no sea en el futuro
  const now = new Date();
  if (date > now) {
    return false;
  }

  // Verificar que no sea muy antigua (más de 150 años)
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 150);
  if (date < minDate) {
    return false;
  }

  return true;
}

/**
 * Valida un nombre
 * @param {string} name - Nombre a validar
 * @returns {boolean} - true si es válido
 */
export function validateName(name) {
  if (!name || typeof name !== 'string') {
    return false;
  }

  // Eliminar espacios extra y verificar longitud
  const trimmedName = name.trim();
  if (trimmedName.length < 2 || trimmedName.length > 50) {
    return false;
  }

  // Verificar que solo contenga letras, espacios y algunos caracteres especiales
  const nameRegex = /^[a-zA-ZÀ-ÿ\u00f1\u00d1\s'-]+$/;
  return nameRegex.test(trimmedName);
}

/**
 * Valida una ocupación o array de ocupaciones
 * @param {string|Array<string>} occupation - Ocupación(es) a validar
 * @returns {boolean} - true si es válida
 */
export function validateOccupation(occupation) {
  const validOccupations = [
    'empleado/a',
    'emprendedor/a',
    'ventas',
    'estudiante',
    'hogar',
    'creador/a',
    'artista',
    'diseñador/a',
    'escritor/a',
    'músico/a',
    'actor/actriz',
    'fotógrafo/a',
    'profesional independiente',
    'gerente/directivo',
    'consultor/a',
    'coach/mentor',
    'terapeuta',
    'médico/a',
    'abogado/a',
    'ingeniero/a',
    'arquitecto/a',
    'docente',
    'investigador/a',
    'otro'
  ];

  // Si es un array (nuevo formato)
  if (Array.isArray(occupation)) {
    // Debe tener entre 1 y 2 elementos
    if (occupation.length < 1 || occupation.length > 2) {
      return false;
    }
    // Todos los elementos deben ser ocupaciones válidas
    return occupation.every(occ => validOccupations.includes(occ));
  }
  
  // Si es un string (formato antiguo - compatibilidad)
  if (typeof occupation === 'string') {
    return validOccupations.includes(occupation);
  }

  return false;
}

/**
 * Valida un estado civil
 * @param {string} status - Estado civil a validar
 * @returns {boolean} - true si es válido
 */
export function validateStatus(status) {
  if (!status || typeof status !== 'string') {
    return false;
  }

  const validStatuses = [
    'soltero/a',
    'en relación',
    'casado/a',
    'divorciado/a',
    'viudo/a'
  ];

  return validStatuses.includes(status);
}

/**
 * Valida todos los datos del usuario
 * @param {Object} userData - Datos del usuario
 * @returns {Object} - { isValid: boolean, errors: string[] }
 */
export function validateUserData(userData) {
  const errors = [];

  if (!userData) {
    return { isValid: false, errors: ['Datos de usuario no proporcionados'] };
  }

  // Validar nombre
  if (!validateName(userData.name)) {
    errors.push('Nombre inválido');
  }

  // Validar apellido paterno
  if (!validateName(userData.apellidoPaterno)) {
    errors.push('Apellido paterno inválido');
  }

  // Validar apellido materno (opcional)
  if (userData.apellidoMaterno && !validateName(userData.apellidoMaterno)) {
    errors.push('Apellido materno inválido');
  }

  // Validar fecha de nacimiento
  if (!validateBirthDate(userData.birth)) {
    errors.push('Fecha de nacimiento inválida');
  }

  // Validar ocupación
  if (!validateOccupation(userData.occupation)) {
    errors.push('Ocupación inválida');
  }

  // Validar estado civil
  if (!validateStatus(userData.status)) {
    errors.push('Estado civil inválido');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Sanitiza un string para prevenir XSS
 * @param {string} str - String a sanitizar
 * @returns {string} - String sanitizado
 */
export function sanitizeString(str) {
  if (typeof str !== 'string') {
    return '';
  }

  return str
    .replace(/[<>]/g, '') // Remover < y >
    .trim()
    .slice(0, 1000); // Limitar longitud
}

/**
 * Sanitiza los datos del usuario
 * @param {Object} userData - Datos del usuario
 * @returns {Object} - Datos sanitizados
 */
export function sanitizeUserData(userData) {
  if (!userData) {
    return null;
  }

  // Sanitizar occupation si es array
  let sanitizedOccupation = userData.occupation;
  if (Array.isArray(userData.occupation)) {
    sanitizedOccupation = userData.occupation.map(occ => sanitizeString(occ));
  }

  return {
    name: sanitizeString(userData.name),
    apellidoPaterno: sanitizeString(userData.apellidoPaterno),
    apellidoMaterno: sanitizeString(userData.apellidoMaterno),
    birth: userData.birth, // La fecha ya está validada
    occupation: sanitizedOccupation, // Ya validada y sanitizada
    status: userData.status // Ya validada
  };
}
